import { useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useApi } from '../api/useApi';
import { useAuth } from '../auth/AuthProvider';
import { SignInPanel } from '../auth/SignInPanel';
import { CheckCircleIcon } from '../components/icons';
import { Popup } from '../components/Popup';
import { fetchLoginConfig } from '../legacy-api/chat';
import { LegacyApiError, legacyFairDomain } from '../legacy-api/client';
import { enterGiveaway } from '../legacy-api/giveaway';

import { PrizeBadge } from './TreasureHuntIntro';
import type { CaughtIcon } from './TreasureHuntProvider';

/**
 * The legacy `PrizeForm`: entries are tied to the visitor's account, so it asks
 * them to sign in first, the same way the chat does.
 */
export function TreasureClaim({
  caught,
  question,
  onEntered,
  onClose,
}: {
  caught: CaughtIcon;
  /** The fair's extra question; null asks nothing. */
  question: string | null;
  /** The entry was accepted; the thanks message stays up until closed. */
  onEntered: () => void;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [entered, setEntered] = useState(false);

  return (
    <Popup open onClose={onClose} label={t('treasureHunt.title')}>
      <PrizeBadge />
      <img
        src={caught.icon.imageUrl}
        alt={caught.icon.name}
        className="mx-auto mt-6 size-20 rounded-full"
      />

      {entered ? (
        <div className="mt-6" role="status">
          <CheckCircleIcon className="mx-auto size-12 text-online" />
          <h2 className="mt-3 text-2xl font-bold text-fog-100">{t('treasureHunt.thanksTitle')}</h2>
          <p className="mt-2 text-fog-500">{t('treasureHunt.thanksBody')}</p>
        </div>
      ) : (
        <>
          <h2 className="mt-5 text-3xl font-extrabold text-fog-100">
            {t('treasureHunt.caughtTitle')}
          </h2>
          {/* A fair that asks a question rewards the best answers rather than
              drawing winners at random. */}
          <p className="mt-2 text-fog-500">
            {t(question === null ? 'treasureHunt.caughtBody' : 'treasureHunt.caughtBodyQuestion')}
          </p>
          {token === undefined ? (
            <SignIn />
          ) : (
            <EntryForm
              caught={caught}
              question={question}
              token={token}
              onEntered={() => {
                setEntered(true);
                onEntered();
              }}
            />
          )}
        </>
      )}
    </Popup>
  );
}

function SignIn(): ReactElement {
  const { t } = useTranslation();
  const config = useApi(() => fetchLoginConfig(legacyFairDomain()));

  if (config.data === undefined) {
    return (
      <p className="py-6 text-fog-500">
        {config.loading ? t('common.loading') : t('common.error')}
      </p>
    );
  }

  return (
    <SignInPanel
      config={config.data}
      title={t('chat.signInTitle')}
      body={t('treasureHunt.signInBody')}
    />
  );
}

function EntryForm({
  caught,
  question,
  token,
  onEntered,
}: {
  caught: CaughtIcon;
  question: string | null;
  token: string;
  onEntered: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const [email, setEmail] = useState('');
  const [nameAndCity, setNameAndCity] = useState('');
  const [phone, setPhone] = useState('');
  const [answer, setAnswer] = useState('');
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);

  const submit = (event: FormEvent): void => {
    event.preventDefault();

    if (sending) {
      return;
    }

    setSending(true);
    setFailed(false);
    enterGiveaway(token, {
      giveawayId: caught.icon.id,
      standId: caught.standId,
      email: email.trim(),
      phone: phone.trim(),
      nameAndCity: nameAndCity.trim(),
      ...(question !== null && { answer: answer.trim() }),
    })
      .then(onEntered)
      .catch((cause: unknown) => {
        // An expired session: signing out brings back the sign-in buttons, and
        // the details typed so far are lost with the form, as in the legacy app.
        if (cause instanceof LegacyApiError && cause.unauthorized) {
          signOut();
        } else {
          setFailed(true);
        }
      })
      .finally(() => {
        setSending(false);
      });
  };

  const inputClass =
    'mt-1 w-full rounded-xl bg-ink-850 px-4 py-3 text-fog-100 placeholder:text-fog-600';

  return (
    <form onSubmit={submit} className="mt-6 space-y-4 text-left">
      <label className="block text-sm text-fog-500">
        {t('treasureHunt.email')}
        <input
          type="email"
          required
          autoComplete="email"
          maxLength={150}
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
          className={inputClass}
        />
      </label>
      <label className="block text-sm text-fog-500">
        {t('treasureHunt.nameAndCity')}
        <input
          required
          autoComplete="name"
          maxLength={200}
          value={nameAndCity}
          onChange={(event) => {
            setNameAndCity(event.target.value);
          }}
          className={inputClass}
        />
      </label>
      <label className="block text-sm text-fog-500">
        {t('treasureHunt.phone')}
        <input
          type="tel"
          required
          autoComplete="tel"
          maxLength={100}
          value={phone}
          onChange={(event) => {
            setPhone(event.target.value);
          }}
          className={inputClass}
        />
      </label>
      {question !== null && (
        <label className="block text-sm text-fog-500">
          {question}
          <textarea
            required
            rows={3}
            maxLength={2000}
            value={answer}
            onChange={(event) => {
              setAnswer(event.target.value);
            }}
            className={`${inputClass} resize-none`}
          />
        </label>
      )}
      {failed && (
        <p role="alert" className="text-sm text-red-400">
          {t('treasureHunt.failed')}
        </p>
      )}
      <button
        type="submit"
        disabled={sending}
        className="w-full rounded-xl bg-prize py-3.5 font-semibold text-ink-950 transition-opacity disabled:opacity-50"
      >
        {t('treasureHunt.submit')}
      </button>
    </form>
  );
}
